import * as React from "react"
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { OpportunitySet } from "../../../types/opportunities.types"
import { useRelativeTime, formatFullLocalDateTime, formatDurationMs } from "../../../lib/format-relative-time.js"
import { humanizeBrandName, normalizeAdPlatforms } from "../../../lib/format-opportunity.js"
import { SetDateWindowMeta } from "../SetDateWindowMeta/SetDateWindowMeta.js"
import { SetParamsPopover } from "../SetParamsPopover/SetParamsPopover.js"

export interface OpportunitySetCardProps {
  set: OpportunitySet
  onClick?: () => void
  onRename?: (set: OpportunitySet) => void
  canRename?: boolean
  onDelete?: (set: OpportunitySet) => void
  canDelete?: boolean
  isGenerating?: boolean
  isJustCompleted?: boolean
  isError?: boolean
  /** Backend-provided error message for FAILED sets. Shown truncated on the card with a `title` tooltip for the full string. */
  errorMessage?: string
  /** Human-readable My Industry label (e.g., "Ingrown Hair"). When null, no industry pill renders. */
  industryLabel?: string | null
  /** Human-readable Benchmark Industry label. When set and different from industryLabel, a second pill renders. */
  benchmarkIndustryLabel?: string | null
  onRetry?: (set: OpportunitySet) => void
  onRefresh?: (set: OpportunitySet) => void
  canRefresh?: boolean
  /** Unix ms when generation started — renders a live elapsed clock in the Generating label. Persisted by the caller so refresh keeps counting. */
  generationStartedAt?: number
}

const DOT_DELAYS = ["-0.3s", "-0.15s", "0s"] as const

const PROGRESS_BAR_DURATION_MS = 600
/** Next segment starts when the previous width animation finishes (absolute layout avoids flex squeeze). */
const progressBarDelayMs = (segmentIndex: number) => segmentIndex * PROGRESS_BAR_DURATION_MS

function formatElapsed(ms: number): string {
  return formatDurationMs(ms)
}

/**
 * Auto-generated default names look like `Set YYMMDD-HHMM` (e.g. "Set 260423-1342").
 * That string is unreadable in the UI. When the name matches the pattern we render
 * a humanized "Apr 23, 1:42 PM" subtitle. The subtitle is derived from the
 * authoritative `createdAt` ISO string (always UTC) rendered in the viewer's locale —
 * NOT by parsing the name itself, whose digits are wall-clock values from whatever
 * time zone the server happened to run in (UTC in prod). Custom user-chosen names
 * fall through unchanged.
 */
const DEFAULT_NAME_RE = /^Set \d{6}-\d{4}$/
function prettifyDefaultName(name: string, createdAt: string): { prefix: string; date: string } | null {
  if (!DEFAULT_NAME_RE.test(name)) return null
  const date = formatFullLocalDateTime(createdAt)
  if (!date) return null
  return { prefix: "Set", date }
}


function useElapsed(startedAt: number | undefined, active: boolean): string | null {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    if (!active || !startedAt) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active, startedAt])
  if (!active || !startedAt) return null
  return formatElapsed(now - startedAt)
}

export function OpportunitySetCard({
  set, onClick, onRename, canRename = true, onDelete, canDelete = true,
  isGenerating, isJustCompleted, isError, errorMessage, industryLabel, benchmarkIndustryLabel,
  onRetry, onRefresh, canRefresh = true, generationStartedAt,
}: OpportunitySetCardProps) {
  const industryLabelForPopover = industryLabel ?? null
  const benchmarkIndustryLabelForPopover = benchmarkIndustryLabel ?? null
  const { t } = useTranslation()
  const elapsed = useElapsed(generationStartedAt, !!isGenerating)
  const relativeDate = useRelativeTime(set.createdAt)
  const pretty = prettifyDefaultName(set.name, set.createdAt)
  const subtitleDate = pretty?.date ?? formatFullLocalDateTime(set.createdAt) ?? ""
  const createdDate = formatFullLocalDateTime(set.createdAt) ?? ""

  const canShowRefresh = !!onRefresh && canRefresh && !isError && set.status === "READY" && set.totalCount > 0
  const hasActions = !isGenerating && (!!onRename || !!onDelete || (!!onRetry && !isError && set.totalCount === 0) || canShowRefresh)
  const isEmpty = !isGenerating && !isError && set.totalCount === 0
  const isInteractive = !!onClick && !isGenerating && !isError && !isEmpty

  const [barReady, setBarReady] = React.useState(false)
  React.useEffect(() => {
    setBarReady(false)
    const id = requestAnimationFrame(() => setBarReady(true))
    return () => cancelAnimationFrame(id)
  }, [
    set.id,
    set.activeCount,
    set.completedScriptsCount,
    set.rejectedCount,
    set.totalCount,
  ])

  const progressSegments = React.useMemo(() => {
    const items: { key: string; count: number; colorClass: string }[] = []
    if (set.activeCount > 0) items.push({ key: "active", count: set.activeCount, colorClass: "bg-status-success/70" })
    if (set.completedScriptsCount > 0) {
      items.push({ key: "scripts", count: set.completedScriptsCount, colorClass: "bg-primary/40" })
    }
    if (set.rejectedCount > 0) items.push({ key: "rejected", count: set.rejectedCount, colorClass: "bg-destructive/50" })

    // Stats can overlap (e.g. active opps + script rows); normalize so segments fill the bar.
    const barTotal = items.reduce((sum, item) => sum + item.count, 0)
    if (barTotal <= 0) return []

    let leftPct = 0
    return items.map((item) => {
      const pct = (item.count / barTotal) * 100
      const segment = { key: item.key, pct, leftPct, colorClass: item.colorClass }
      leftPct += pct
      return segment
    })
  }, [set.activeCount, set.completedScriptsCount, set.rejectedCount])

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) return
    if (e.target !== e.currentTarget) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onClick()
    }
  }


  // ── Error state ────────────────────────────────────────────────────
  if (isError) {
    return (
      <div
        className="flex w-full flex-col rounded-md border border-destructive/25 bg-destructive/[0.025] p-3.5 motion-safe:animate-[error-shake_0.5s_ease-out] motion-reduce:animate-none"
        role="alert"
      >
        {/* Header: icon + name + actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <Icon
              name="alert-circle"
              size="sm"
              color="destructive"
              className="shrink-0 motion-safe:animate-[error-icon-pulse_2.5s_ease-in-out_infinite] motion-reduce:opacity-80"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="min-w-0 truncate text-sm font-semibold text-foreground">{set.name}</span>
              <SetDateWindowMeta startDate={set.startDate} endDate={set.endDate} />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <SetParamsPopover set={set} industryLabel={industryLabelForPopover} />
            {(onRetry || onDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0 text-muted-foreground transition-colors hover:text-foreground min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                    aria-label={t("common.more", "More")}
                  >
                    <Icon name="more-horizontal" size="sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {onRetry && (
                    <DropdownMenuItem
                      icon="refresh-cw"
                      onClick={() => onRetry(set)}
                      className="cursor-pointer"
                    >
                      {t("common.retry", "Retry")}
                    </DropdownMenuItem>
                  )}
                  {onRetry && onDelete && <DropdownMenuSeparator />}
                  {onDelete && (
                    <DropdownMenuItem
                      variant="destructive"
                      icon="trash-2"
                      onClick={canDelete ? () => onDelete(set) : undefined}
                      disabled={!canDelete}
                      title={!canDelete ? t("common.noPermission") : undefined}
                      className={canDelete ? "cursor-pointer" : "text-destructive/50"}
                    >
                      {t("common.remove", "Remove")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Error message + date + inline retry */}
        <div className="mt-2 flex flex-col gap-2">
          <p
            className="line-clamp-2 text-xs leading-relaxed text-destructive/75"
            title={errorMessage ?? undefined}
          >
            {errorMessage ?? t("opportunities.setCard.generationFailed")}
          </p>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground/50">{relativeDate}</span>
            {onRetry && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 border-destructive/20 px-2.5 text-[11px] text-destructive/80 hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); onRetry(set) }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Icon name="refresh-cw" size="xs" />
                {t("common.retry", "Retry")}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Default / Generating / Empty card ─────────────────────────────
  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-busy={isGenerating || undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={handleCardKeyDown}
      className={cn(
        "group/card relative flex h-full w-full flex-col overflow-hidden rounded-md border p-3.5 text-left",
        "transition-[box-shadow,border-color,background-color] duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        isEmpty
          ? "border-border/40 bg-muted/30 shadow-none"
          : "border-border/60 bg-card shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]",
        isGenerating && "border-primary/15 shadow-none",
        isJustCompleted && "motion-safe:animate-[aura-pulse_2s_ease-out] motion-reduce:border-primary/30",
        isInteractive && "cursor-pointer hover:border-primary/25 hover:shadow-[0_6px_24px_rgba(0,0,0,0.09),0_2px_8px_rgba(0,0,0,0.05)]",
      )}
      aria-label={isInteractive ? t("opportunities.setCard.openSetAria", { name: set.name }) : undefined}
    >
      {/* Hover accent bar — top gradient stripe visible on interactive cards */}
      {isInteractive && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px] rounded-t-md bg-gradient-to-r from-transparent via-primary/45 to-transparent opacity-0 transition-opacity duration-200 group-hover/card:opacity-100"
          aria-hidden
        />
      )}

      {/* Generating overlays */}
      {isGenerating && (
        <>
          <div
            className="pointer-events-none absolute inset-0 rounded-md motion-safe:animate-[osc-mesh-flow_4s_ease-in-out_infinite] motion-reduce:opacity-50"
            style={{
              background: "linear-gradient(135deg, color-mix(in oklch, var(--primary) 12%, transparent) 0%, color-mix(in oklch, var(--primary) 8%, transparent) 25%, color-mix(in oklch, var(--primary) 5%, transparent) 50%, color-mix(in oklch, var(--primary) 10%, transparent) 75%, color-mix(in oklch, var(--primary) 6%, transparent) 100%)",
              backgroundSize: "300% 300%",
            }}
          />
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md motion-reduce:hidden" aria-hidden>
            <div
              className="absolute inset-y-0 -left-1/2 w-1/2 animate-[osc-shimmer-sweep_3s_ease-in-out_infinite]"
              style={{
                background: "linear-gradient(90deg, transparent 0%, color-mix(in oklch, var(--primary) 6%, transparent) 40%, color-mix(in oklch, var(--primary) 10%, transparent) 50%, color-mix(in oklch, var(--primary) 6%, transparent) 60%, transparent 100%)",
              }}
            />
          </div>
        </>
      )}

      {/* Cancel button for generating state — outside the blurred content div so filter: blur() doesn't reach it */}
      {isGenerating && onDelete && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="absolute right-2 top-2 z-10 text-muted-foreground/50 transition-colors hover:text-destructive min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
          aria-label={t("opportunities.setCard.cancelGeneration", "Cancel generation")}
          disabled={!canDelete}
          onClick={(e) => { e.stopPropagation(); if (canDelete) onDelete(set) }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Icon name="x" size="sm" />
        </Button>
      )}

      {/* Card content — same structure for default & generating */}
      <div className={cn(
        "relative flex flex-1 flex-col",
        isGenerating && "select-none motion-safe:animate-[osc-content-materialize_3s_ease-in-out_infinite] motion-reduce:opacity-30 motion-reduce:blur-sm",
      )}>
        {/* Header: title block + actions */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {/* Row 1: set name */}
            <span className={cn(
              "min-w-0 truncate text-sm font-semibold leading-snug",
              isEmpty ? "text-foreground/45" : "text-foreground",
            )}>
              {set.name}
            </span>
            {/* Row 2: generation start — full local date/time */}
            <span className="min-w-0 truncate text-xs font-medium leading-tight text-muted-foreground/60">
              {subtitleDate}
            </span>
            {/* Row 3: industry pill(s) */}
            {industryLabel && (
              <div className="mt-0.5">
                {benchmarkIndustryLabelForPopover ? (
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/40">{t("opportunities.industryLabel")}</span>
                      <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-tight tracking-wide text-primary" title={industryLabel}>
                        <span className="max-w-[5.5rem] truncate uppercase">{industryLabel}</span>
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[9px] uppercase tracking-wider text-muted-foreground/40">{t("opportunities.benchmarkIndustryLabel")}</span>
                      <span className="inline-flex items-center rounded-md bg-violet-subtle px-1.5 py-0.5 text-[10px] font-medium leading-tight tracking-wide text-violet-subtle-foreground" title={benchmarkIndustryLabelForPopover}>
                        <span className="max-w-[5.5rem] truncate uppercase">{benchmarkIndustryLabelForPopover}</span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <span
                    className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium leading-tight tracking-wide text-primary"
                    title={industryLabel}
                  >
                    <span className="max-w-[8rem] truncate uppercase">{industryLabel}</span>
                  </span>
                )}
              </div>
            )}
            <SetDateWindowMeta startDate={set.startDate} endDate={set.endDate} className="mt-0.5" />
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {!isGenerating && (
              <SetParamsPopover set={set} industryLabel={industryLabelForPopover} benchmarkIndustryLabel={benchmarkIndustryLabelForPopover} />
            )}
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className={cn(
                      "text-muted-foreground transition-[opacity,color] hover:text-foreground",
                      "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7",
                      "opacity-0 group-hover/card:opacity-100 group-focus-visible/card:opacity-100 [@media(hover:none)]:opacity-100",
                    )}
                    aria-label={t("common.more", "More")}
                  >
                    <Icon name="more-horizontal" size="sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {isEmpty && onRetry && (
                    <DropdownMenuItem
                      icon="refresh-cw"
                      onClick={() => onRetry(set)}
                      className="cursor-pointer"
                    >
                      {t("common.retry", "Retry")}
                    </DropdownMenuItem>
                  )}
                  {canShowRefresh && (
                    <DropdownMenuItem
                      icon="refresh-cw"
                      onClick={() => onRefresh(set)}
                      className="cursor-pointer"
                    >
                      {t("opportunities.regenSet", "Regenerate opportunities")}
                    </DropdownMenuItem>
                  )}
                  {onRename && (
                    <DropdownMenuItem
                      icon="pencil"
                      onClick={canRename ? () => onRename(set) : undefined}
                      disabled={!canRename}
                      title={!canRename ? t("common.noPermission") : undefined}
                      className={canRename ? "cursor-pointer" : ""}
                    >
                      {t("common.rename", "Rename")}
                    </DropdownMenuItem>
                  )}
                  {onDelete && ((isEmpty && onRetry) || canShowRefresh || onRename) && <DropdownMenuSeparator />}
                  {onDelete && (
                    <DropdownMenuItem
                      variant="destructive"
                      icon="trash-2"
                      onClick={canDelete ? () => onDelete(set) : undefined}
                      disabled={!canDelete}
                      title={!canDelete ? t("common.noPermission") : undefined}
                      className={canDelete ? "cursor-pointer" : "text-destructive/50"}
                    >
                      {t("common.remove", "Remove")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Meta row: creator · relative time */}
        <div className="mt-1.5 flex items-center justify-between gap-2">
          {set.createdByName ? (
            <span
              className="min-w-0 truncate text-[11px] font-medium leading-tight text-muted-foreground/60"
              title={t("opportunities.setCard.createdByWithDate", { name: set.createdByName, date: createdDate })}
            >
              {set.createdByName}
            </span>
          ) : <span />}
          <span className="shrink-0 tabular-nums text-[11px] leading-tight text-muted-foreground/45">
            {relativeDate}
          </span>
        </div>

        {/* Products / platforms context tags */}
        {(() => {
          const adPlatformsArr = normalizeAdPlatforms(set.adPlatforms)
          return !isGenerating && (set.products?.length || adPlatformsArr.length) ? (
            <div className="mt-2 flex flex-col gap-1">
              {set.products?.length ? (
                <TagRow label={t("opportunities.setCard.productsLabel", "Products")} tags={set.products} />
              ) : null}
              {adPlatformsArr.length ? (
                <TagRow
                  label={t("opportunities.setCard.adPlatformsLabel", "Platforms")}
                  tags={adPlatformsArr.map(humanizeBrandName)}
                />
              ) : null}
            </div>
          ) : null
        })()}

        {/* Stats / empty state — always pinned to the bottom */}
        <div className="mt-auto border-t border-border/30 pt-3">
          {isGenerating ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <StatBlock value={0} label={t("opportunities.setCard.totalLabel", "total")} />
              <StatBlock value={0} label={t("opportunities.setCard.activeLabel", "active")} color="success" />
              <StatBlock value={0} label={t("opportunities.setCard.slidesLabel", "slides")} color="primary" />
            </div>
          ) : isEmpty ? (
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground/45">
              <Icon name="lightbulb" size="xs" className="shrink-0" aria-hidden />
              {t("opportunities.setCard.noOpportunities", "No opportunities")}
            </span>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <StatBlock value={set.totalCount} label={t("opportunities.setCard.totalLabel", "total")} />
                {set.activeCount > 0 && set.activeCount !== set.totalCount && (
                  <StatBlock value={set.activeCount} label={t("opportunities.setCard.activeLabel", "active")} color="success" />
                )}
                {set.completedScriptsCount > 0 && (
                  <StatBlock value={set.completedScriptsCount} label={t("opportunities.setCard.scriptsLabel", "scripts")} color="primary" />
                )}
                {set.rejectedCount > 0 && (
                  <StatBlock value={set.rejectedCount} label={t("opportunities.setCard.rejectedLabel", "rejected")} color="destructive" />
                )}
              </div>
              {progressSegments.length > 0 && (
                <div className="relative mt-2.5 h-2 w-full overflow-hidden rounded-full bg-border/35" aria-hidden>
                  {progressSegments.map((segment, index) => (
                    <div
                      key={segment.key}
                      className={cn(
                        "absolute top-0 h-full",
                        segment.colorClass,
                        index === 0 && "rounded-l-full",
                        index === progressSegments.length - 1 && "rounded-r-full",
                      )}
                      style={{
                        left: `${segment.leftPct}%`,
                        width: barReady ? `${segment.pct}%` : "0%",
                        transition: `width ${PROGRESS_BAR_DURATION_MS}ms cubic-bezier(0.4,0,0.2,1)`,
                        transitionDelay: `${progressBarDelayMs(index)}ms`,
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Generating label */}
      {isGenerating && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex items-center gap-2.5 rounded-full bg-card/90 px-4 py-2 shadow-sm ring-1 ring-primary/15 backdrop-blur-sm">
            <div className="flex items-end gap-1 motion-reduce:hidden" aria-hidden>
              {DOT_DELAYS.map((delay) => (
                <span
                  key={delay}
                  className="h-1.5 w-1.5 rounded-full bg-primary motion-safe:animate-bounce"
                  style={{ animationDelay: delay, animationDuration: "0.9s" }}
                />
              ))}
            </div>
            <span className="text-xs font-semibold text-primary/80">
              {t("opportunities.setCard.generating")}
            </span>
            {elapsed && (
              <span
                className="text-xs font-medium tabular-nums text-primary/60"
                aria-label={t("opportunities.setCard.elapsedAria", { time: elapsed })}
              >
                {elapsed}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tag Row ─────────────────────────────────────────────────────────

const MAX_VISIBLE_TAGS = 3

function TagRow({ label, tags }: { label: string; tags: string[] }) {
  const visible = tags.slice(0, MAX_VISIBLE_TAGS)
  const overflow = tags.length - MAX_VISIBLE_TAGS
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/45">{label}</span>
      {visible.map((tag) => (
        <span
          key={tag}
          className="inline-flex max-w-[9rem] items-center truncate rounded-md bg-muted/70 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-muted-foreground/80"
          title={tag}
        >
          {tag}
        </span>
      ))}
      {overflow > 0 && (
        <span className="shrink-0 text-[10px] font-semibold text-muted-foreground/45">+{overflow}</span>
      )}
    </div>
  )
}

// ─── Stat Block ──────────────────────────────────────────────────────

type StatColor = "default" | "success" | "primary" | "destructive"

const STAT_VALUE_STYLES: Record<StatColor, string> = {
  default: "text-foreground",
  success: "text-status-success",
  primary: "text-primary",
  destructive: "text-destructive/75",
}

const STAT_DOT_STYLES: Record<StatColor, string> = {
  default: "bg-foreground/30",
  success: "bg-status-success/70",
  primary: "bg-primary/65",
  destructive: "bg-destructive/55",
}

function StatBlock({ value, label, color = "default" }: { value: number; label: string; color?: StatColor }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {color !== "default" && (
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STAT_DOT_STYLES[color])} aria-hidden />
      )}
      <span className={cn("text-sm font-medium leading-none tabular-nums", STAT_VALUE_STYLES[color])}>
        {value}
      </span>
      <span className="text-[11px] font-medium leading-none text-muted-foreground/55">{label}</span>
    </span>
  )
}

// ─── Skeleton ────────────────────────────────────────────────────────

export function OpportunitySetCardSkeleton() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-md border border-border/60 bg-card p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
      {/* Header: title + action icon */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
          {/* Industry pill */}
          <div className="mt-0.5">
            <Skeleton className="h-[18px] w-16 rounded-md" />
          </div>
        </div>
        <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
      </div>
      {/* Meta: creator · time */}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-1/6" />
      </div>
      {/* Tag row: platforms (typical card — products are optional) */}
      <div className="mt-2 flex items-center gap-1">
        <Skeleton className="h-3 w-14 shrink-0" />
        <Skeleton className="h-[18px] w-16 rounded-md" />
      </div>
      {/* Stats — pinned to bottom like the real card */}
      <div className="mt-auto border-t border-border/30 pt-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <Skeleton className="h-3.5 w-14 rounded-full" />
          <Skeleton className="h-3.5 w-16 rounded-full" />
          <Skeleton className="h-3.5 w-14 rounded-full" />
        </div>
        {/* Progress bar */}
        <Skeleton className="mt-2.5 h-2 w-full rounded-full" />
      </div>
    </div>
  )
}
